<template>
  <div class="card">
    <div class="card-header">
      <div class="container-fluid">
        <div class="row d-flex justify-content-between">
          <div class="mb-0 mt-2">
            <span class="title-row mr-1" @click="onStarClick()">
              <fa class="gold-star mb-1" :icon="['fas', 'star']" fixed-width v-if="report && report.isStarred" />
              <fa class="mb-1" :icon="['far', 'star']" fixed-width v-if="!report || (report && !report.isStarred)" />
            </span>
            <h6 class="title-row">
              <b>{{ header }}</b> <span class="badge" :class="statusClass">{{ report.statusDisplayName }}</span>
            </h6>
          </div>
          <div>
            <ul class="nav nav-pills card-header-pills justify-content-end">
              <li v-if="canOpen" class="nav-item">
                <button type="button" class="btn btn-sm btn-link" @click="open">
                  <fa icon="external-link-alt" />
                  View Report
                </button>
              </li>
              <li v-if="canResume" class="nav-item">
                <button type="button" class="btn btn-sm btn-link" @click="resume" :disabled="isResuming">
                  <fa icon="play" />
                  Resume
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!isExpanded" class="card-body">
      <div class="container-fluid">
        <div class="row">
          <div class="col">
            <ul class="list-group list-group-horizontal">
              <li class="list-group-item"><b>Date:</b> {{ formatter.date.short(report.createdOn) || "---" }}</li>
              <li class="list-group-item"><b>Started:</b> {{ formatter.date.medium(report.startedOn) || "---" }}</li>
              <li class="list-group-item">
                <b>Final Score:</b> <span :class="finalScoreClass">{{ report.scoreFinalDisplayName || "---" }}</span>
              </li>
              <li class="list-group-item">
                <b>Status:</b> <span :class="decisionClass">{{ report.decisionDisplayName || "---" }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isExpanded" class="card-body">
      <div class="container-fluid">
        <div class="row">
          <div class="col-12 col-md-6 col-lg-8">
            <div class="row">
              <div class="col-12 col-md-12 col-lg-6">
                <ul class="list-group list-group-flush">
                  <li class="list-group-item"><b>Date:</b> {{ formatter.date.short(report.createdOn) || "---" }}</li>
                  <li class="list-group-item"><b>Type:</b> {{ report.testType || "---" }}</li>
                  <li class="list-group-item"><b>Reference:</b> #{{ report.referenceNumber || "---" }}</li>
                  <li class="list-group-item"><b>Email:</b> {{ report.emailAddress || "---" }}</li>
                  <li v-if="hasInvestigationId" class="list-group-item"><b>Investigation Id:</b> {{ report.investigationId || "---" }}</li>
                  <li class="list-group-item"><b>User:</b> {{ report.testBy || "---" }}</li>
                </ul>
              </div>
              <div class="col-12 col-md-12 col-lg-6">
                <ul class="list-group list-group-flush">
                  <li class="list-group-item"><b>Region:</b> {{ report.region || "---" }}</li>
                  <li class="list-group-item"><b>Facility/Store:</b> {{ report.facility || "---" }}</li>
                  <li class="list-group-item"><b>Function/District:</b> {{ report.function || "---" }}</li>
                  <li class="list-group-item"><b>Company/Division:</b> {{ report.company || "---" }}</li>
                  <li class="list-group-item"><b>Country:</b> {{ report.countryDisplayName || "---" }}</li>
                  <li class="list-group-item"><b>State:</b> {{ report.stateDisplayName || "---" }}</li>
                </ul>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6 col-lg-4">
            <ul class="list-group list-group-flush">
              <li class="list-group-item"><b>Started:</b> {{ formatter.date.medium(report.startedOn) || "---" }}</li>
              <li class="list-group-item"><b>Duration:</b> {{ formatter.duration.default(report.durationInSeconds, "seconds") || "---" }}</li>
              <li class="list-group-item">
                <b>Original Score:</b> <span :class="originalScoreClass">{{ report.scoreOriginalDisplayName || "---" }}</span>
              </li>
              <li class="list-group-item">
                <b>Final Score:</b> <span :class="finalScoreClass">{{ report.scoreFinalDisplayName || "---" }}</span>
              </li>
              <li class="list-group-item">
                <b>Status:</b> <span :class="decisionClass">{{ report.decisionDisplayName || "---" }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div class="card-footer text-center">
      <button v-if="!isExpanded" @click="toggleExpand" type="button" class="btn btn-link btn-sm pt-0 pb-1"><fa icon="chevron-down" /> View Details</button>
      <button v-if="isExpanded" @click="toggleExpand" type="button" class="btn btn-link btn-sm pt-0 pb-1"><fa icon="chevron-up" /> Hide Details</button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import formatter from "@/utils/formatter";

export default {
  props: {
    organizationId: String,
    report: Object,
  },
  data() {
    return {
      isExpanded: false,
      isResuming: false,
    };
  },
  computed: {
    ...mapGetters("role", [
      "isVerensicsAdministrator",
      "isOrganizationAdministrator",
      "isReportCreator",
      "isReportViewer",
      "isReportCommenter",
      "isReportDecisionEditor",
      "isReportAnswerEditor",
    ]),
    formatter: () => formatter,
    header() {
      var header = "";
      if (this.isVerensicsAdministrator) header += `${this.report.organization} - `;
      header += `${this.report.registrationId}`;
      return header;
    },
    statusClass() {
      if (this.report.statusCode == "created") return "badge-info";
      if (this.report.statusCode == "sent-by-email") return "badge-info";
      if (this.report.statusCode == "in-progress") return "badge-warning";
      if (this.report.statusCode == "declined") return "badge-danger";
      if (this.report.statusCode == "completed") return "badge-primary";
      return "badge-secondary";
    },
    isInProgress() {
      return this.report.statusCode == "in-progress";
    },
    hasInvestigationId() {
      return this.report.investigationId != null;
    },
    canOpen() {
      var isAuthorized =
        this.isVerensicsAdministrator ||
        this.isOrganizationAdministrator ||
        this.isReportViewer ||
        this.isReportCommenter ||
        this.isReportDecisionEditor ||
        this.isReportAnswerEditor;
      if (!isAuthorized) return false;
      if (this.isInProgress && !this.isVerensicsAdministrator) return false;
      return true;
    },
    canResume() {
      var isAuthorized = this.isVerensicsAdministrator || this.isOrganizationAdministrator || this.isReportCreator;
      var isNotCompleted = this.report.statusCode !== "declined" && this.report.statusCode !== "completed";
      return isAuthorized && isNotCompleted;
    },
    originalScoreClass() {
      if (this.report.scoreOriginalCode === "above-conditions") return ["text-success"];
      if (this.report.scoreOriginalCode === "requires-clarification") return ["text-warning"];
      if (this.report.scoreOriginalCode === "below-conditions") return ["text-danger"];
      if (this.report.scoreOriginalCode === "inconclusive") return [];
      if (this.report.scoreOriginalCode === "declined") return ["text-danger"];
      return [];
    },
    finalScoreClass() {
      if (this.report.scoreFinalCode === "above-conditions") return ["text-success"];
      if (this.report.scoreFinalCode === "requires-clarification") return ["text-warning"];
      if (this.report.scoreFinalCode === "below-conditions") return ["text-danger"];
      if (this.report.scoreFinalCode === "inconclusive") return [];
      if (this.report.scoreFinalCode === "declined") return ["text-danger"];
      return [];
    },
    decisionClass() {
      if (this.report.decisionCode === "hire" || this.report.decisionCode === "no_follow_up_required" || this.report.decisionCode === "handled")
        return ["text-success"];
      if (
        this.report.decisionCode === "decline" ||
        this.report.decisionCode === "follow_up_required" ||
        this.report.decisionCode === "follow_up_required_admissions" ||
        this.report.decisionCode === "follow_up_required_observations"
      )
        return ["text-danger"];
      return ["text-primary"];
    },
  },
  methods: {
    onStarClick() {
      if (!this.report) return;

      // flip the starred value
      this.report.isStarred = !this.report.isStarred;
      let organizationId = this.isVerensicsAdministrator && this.report.organizationId ? this.report.organizationId : this.organizationId;

      // save to server
      this.$axios
        .put(`/v1/organizations/${organizationId}/reports/${this.report.reportId}/star/${this.report.isStarred.toString()}`)
        .catch(err => (this.error = err));
    },
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    open() {
      let organizationId = this.isVerensicsAdministrator && this.report.organizationId ? this.report.organizationId : this.organizationId;
      let routeData = this.$router.resolve(`/organizations/${organizationId}/reports/${this.report.reportId}`);
      window.open(routeData.href, "_blank");
    },
    resume() {
      this.isResuming = true;
      this.$axios
        .post(`/v1/organizations/${this.organizationId}/reports/${this.report.reportId}/resume`)
        .then(res => {
          var reportId = res.data.id;
          var url = res.data.url;
          this.$router.push({ name: "organization-reports-start", params: { organizationId: this.organizationId, reportId: reportId, url: url } });
        })
        .catch(err => (this.error = err))
        .finally(() => (this.isResuming = false));
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

.title-row {
  display: inline-block;
}

.card {
  @extend .mb-0;
  @extend .bg-transparent;
  @extend .border-0;
}

.card-header {
  @extend .py-1;
  @extend .bg-transparent;
  @extend .border-bottom-0;
}

.card-body {
  @extend .px-0;
  @extend .py-2;
}

.card-footer {
  @extend .p-0;
  @extend .bg-transparent;
  @extend .border-top-0;
}

li.list-group-item {
  @extend .pl-2;
  @extend .py-1;
  @extend .my-1;
  @extend .border-0;
  @extend .bg-transparent;
}

.gold-star {
  color: #ffd700;
}
</style>
